/**
 * Детальная карточка одного этапа (приёма) таймлайна.
 *
 * Левая колонка — что входит в этап (чеклист услуг), правая — информация
 * о приёме: плановая/фактическая дата, давление, комментарий, стоимость.
 */
import React from "react";
import {
    IoCalendarClearOutline,
    IoCalendarOutline,
    IoCheckmarkOutline,
    IoDocumentTextOutline,
    IoHeartOutline,
    IoListOutline,
} from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../i18n/config";
import { formatDayMonth, formatRange } from "./planTimelineData";

const STATE_LABELS = {
    done: "plan.timeline.stateDone",
    current: "plan.timeline.stateCurrent",
    future: "plan.timeline.stateFuture",
};

function PatientPlanTimelineStage({ stage, planId }) {
    const { t, i18n } = useTranslation("patient");
    const locale = getLocale(i18n.language);

    if (!stage) return null;

    const percent =
        stage.total > 0 ? Math.round((stage.done / stage.total) * 100) : 0;
    const range = formatRange(stage.start, stage.end);
    const stateLabel = t(STATE_LABELS[stage.state] || STATE_LABELS.future);

    return (
        <div className={`plan-timeline-stage is-${stage.state}`}>
            {/* Шапка этапа: номер-иконка, название, статус, даты. */}
            <div className="plan-timeline-stage__head">
                <div className="plan-timeline-stage__index">
                    {stage.state === "done" ? (
                        <IoCheckmarkOutline aria-hidden="true" size={24} />
                    ) : (
                        stage.number
                    )}
                </div>

                <div className="plan-timeline-stage__meta">
                    <div className="plan-timeline-stage__titles">
                        <h2 className="plan-timeline-stage__title">
                            {stage.title
                                ? t("plan.timeline.stageTitle", {
                                      number: stage.number,
                                      title: stage.title,
                                  })
                                : t("plan.timeline.stageOnly", {
                                      number: stage.number,
                                  })}
                        </h2>
                        <span
                            className={`plan-timeline-stage__badge is-${stage.state}`}>
                            <span className="plan-timeline-stage__dot" />
                            {stateLabel}
                        </span>
                    </div>

                    <div className="plan-timeline-stage__facts">
                        {range ? (
                            <span className="plan-timeline-stage__fact">
                                <IoCalendarOutline
                                    aria-hidden="true"
                                    size={14}
                                />
                                {range}
                            </span>
                        ) : null}
                        <span className="plan-timeline-stage__fact">
                            <IoListOutline aria-hidden="true" size={14} />
                            {t("plan.timeline.completedOf", {
                                done: stage.done,
                                total: stage.total,
                            })}
                        </span>
                        {planId ? (
                            <span className="plan-timeline-stage__fact">
                                <IoDocumentTextOutline
                                    aria-hidden="true"
                                    size={14}
                                />
                                {t("plan.matrix.planNumber", { id: planId })}
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Полоса прогресса этапа. */}
            <div className="plan-timeline-stage__progress">
                <div className="plan-timeline-stage__progress-head">
                    <span>{t("plan.timeline.progress")}</span>
                    <span>{percent}%</span>
                </div>
                <div
                    className="plan-timeline-stage__bar"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent}>
                    <span
                        className="plan-timeline-stage__bar-fill"
                        style={{ width: `${percent}%` }}
                    />
                </div>
            </div>

            {/* Две колонки: услуги этапа и информация о приёме. */}
            <div className="plan-timeline-stage__grid">
                <div className="plan-timeline-stage__col">
                    <div className="plan-timeline-stage__block-title">
                        <IoListOutline aria-hidden="true" size={17} />
                        {t("plan.timeline.includes")}
                    </div>

                    {stage.services.length === 0 ? (
                        <div className="plan-timeline-stage__empty">
                            {t("plan.timeline.noServices")}
                        </div>
                    ) : (
                        <ul className="plan-timeline-checks">
                            {stage.services.map((service) => (
                                <li
                                    key={service.key}
                                    className={`plan-timeline-check${
                                        service.done ? " is-done" : ""
                                    }`}>
                                    <span className="plan-timeline-check__box">
                                        {service.done ? (
                                            <IoCheckmarkOutline
                                                aria-hidden="true"
                                                size={12}
                                            />
                                        ) : null}
                                    </span>
                                    <span className="plan-timeline-check__body">
                                        <span className="plan-timeline-check__name">
                                            {service.name}
                                        </span>
                                        {service.composition ? (
                                            <span className="plan-timeline-check__comp">
                                                {service.composition}
                                            </span>
                                        ) : null}
                                        {service.comment ? (
                                            <span className="plan-timeline-check__comment">
                                                {service.comment}
                                            </span>
                                        ) : null}
                                    </span>
                                    <span className="plan-timeline-check__side">
                                        {service.date ? (
                                            <span className="plan-timeline-check__date">
                                                {formatDayMonth(service.date)}
                                            </span>
                                        ) : null}
                                        <span className="plan-timeline-check__price">
                                            {service.price.toLocaleString(
                                                locale,
                                            )}{" "}
                                            ₽
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="plan-timeline-stage__col">
                    <div className="plan-timeline-stage__block-title">
                        <IoCalendarClearOutline aria-hidden="true" size={17} />
                        {t("plan.timeline.details")}
                    </div>

                    <dl className="plan-timeline-facts">
                        <div className="plan-timeline-facts__row">
                            <dt>{t("plan.timeline.planDate")}</dt>
                            <dd>
                                {formatDayMonth(stage.planDate) ||
                                    t("plan.timeline.notSet")}
                            </dd>
                        </div>
                        <div className="plan-timeline-facts__row">
                            <dt>{t("plan.timeline.actualDate")}</dt>
                            <dd>
                                {formatDayMonth(stage.actualDate) ||
                                    t("plan.timeline.notSet")}
                            </dd>
                        </div>
                        <div className="plan-timeline-facts__row">
                            <dt>
                                <IoHeartOutline aria-hidden="true" size={13} />
                                {t("plan.timeline.pressure")}
                            </dt>
                            <dd>
                                {stage.pressure || t("plan.timeline.notSet")}
                            </dd>
                        </div>
                        <div className="plan-timeline-facts__row">
                            <dt>{t("plan.timeline.servicesTotal")}</dt>
                            <dd>
                                {stage.sum.toLocaleString(locale)} ₽
                            </dd>
                        </div>
                    </dl>

                    <div className="plan-timeline-stage__comment">
                        <div className="plan-timeline-stage__comment-label">
                            {t("plan.timeline.comment")}
                        </div>
                        <div className="plan-timeline-stage__comment-text">
                            {stage.comment || t("plan.timeline.noComment")}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PatientPlanTimelineStage;
